import React, { useMemo } from 'react'
import { WORKSPACE_LOGOS, WORKSPACE_COLORS } from '../config.js'

interface Props {
  workspace: string
  mode: string
  gateState: string
  recallConnected: boolean
  ttsConnected: boolean
  audioReady: boolean
  isSpeaking: boolean
  currentSpeaker: string
  captions: string[]
  llmBadge?: string
  error?: string | null
}

export const BridgeUI: React.FC<Props> = ({
  workspace,
  mode,
  gateState,
  recallConnected,
  ttsConnected,
  audioReady,
  isSpeaking,
  currentSpeaker,
  captions,
  llmBadge,
  error,
}) => {
  const colors = WORKSPACE_COLORS[workspace] || WORKSPACE_COLORS.romea
  const logo = WORKSPACE_LOGOS[workspace] || WORKSPACE_LOGOS.romea

  const statusLabel = useMemo(() => {
    if (isSpeaking) return 'Speaking'
    if (gateState === 'open' || gateState === 'responding') return 'Active'
    if (gateState === 'awaiting_confirmation') return 'Confirm?'
    if (gateState === 'paused') return 'Paused'
    if (!recallConnected) return 'Connecting...'
    return 'Listening'
  }, [isSpeaking, gateState, recallConnected])

  const statusColor = useMemo(() => {
    if (isSpeaking) return '#FF6B35'
    if (gateState === 'open' || gateState === 'responding') return '#00C9A7'
    if (gateState === 'awaiting_confirmation') return '#F59E0B'
    if (!recallConnected) return '#6B7280'
    return '#00C9A7'
  }, [isSpeaking, gateState, recallConnected])

  return (
    <>
    <audio id="tts-audio" autoPlay playsInline style={{ position: 'absolute', opacity: 0 }} />
    <div
      style={{
        ...styles.root,
        background: `radial-gradient(ellipse at 50% 0%, ${colors.primary}22 0%, transparent 60%), #0a0a0f`,
      }}
    >
      {/* Header */}
      <div style={styles.header}>
        <img src={logo} alt={workspace} style={styles.logo} />
        <div style={styles.statusBadge}>
          <div style={{ ...styles.statusDot, backgroundColor: statusColor, ...(isSpeaking ? styles.pulse : {}) }} />
          <span style={{ color: statusColor, fontSize: '14px', fontWeight: 600 }}>{statusLabel}</span>
          {llmBadge && <span style={styles.llmBadge}>{llmBadge}</span>}
        </div>
      </div>

      {/* Connection dots */}
      <div style={styles.dots}>
        <div style={{ ...styles.dot, backgroundColor: audioReady ? '#00C9A7' : '#EF4444' }} />
        <span style={styles.dotLabel}>Mic</span>
        <div style={{ ...styles.dot, backgroundColor: recallConnected ? '#00C9A7' : '#EF4444' }} />
        <span style={styles.dotLabel}>STT</span>
        <div style={{ ...styles.dot, backgroundColor: ttsConnected ? '#00C9A7' : '#EF4444' }} />
        <span style={styles.dotLabel}>TTS</span>
      </div>

      {/* Mode & Gate */}
      <div style={styles.center}>
        <div style={styles.modeBadge}>{mode}</div>

        <div style={styles.gateLabel}>
          Gate: <span style={{ color: gateState === 'open' || gateState === 'responding' ? '#00C9A7' : '#6B7280' }}>{gateState.toUpperCase()}</span>
        </div>
        {currentSpeaker && (
          <div style={styles.speakerLabel}>{currentSpeaker}</div>
        )}
      </div>

      {/* Captions */}
      <div style={styles.captionBanner}>
        {captions.map((line, i) => (
          <div key={i} style={{ ...styles.captionLine, opacity: 0.3 + (i / captions.length) * 0.7 }}>
            {line}
          </div>
        ))}
      </div>

      {/* Error overlay */}
      {error && (
        <div style={styles.errorOverlay}>
          <div style={styles.errorCard}>
            <div style={styles.errorTitle}>Audio Error</div>
            <div style={styles.errorMessage}>{error}</div>
          </div>
        </div>
      )}
    </div>
    </>
  )
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    width: '1280px',
    height: '720px',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    color: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    position: 'absolute',
    top: '24px',
    left: '24px',
    right: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 10,
  },
  logo: {
    height: '32px',
    objectFit: 'contain',
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 16px',
    borderRadius: '20px',
    backgroundColor: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(8px)',
  },
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
  },
  pulse: {
    animation: 'pulse 1.5s infinite',
  },
  llmBadge: {
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '10px',
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: '#9CA3AF',
    fontWeight: 500,
  },
  dots: {
    position: 'absolute',
    top: '24px',
    right: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    zIndex: 10,
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  dotLabel: {
    fontSize: '11px',
    color: '#6B7280',
    fontWeight: 500,
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    zIndex: 10,
  },
  modeBadge: {
    padding: '4px 14px',
    borderRadius: '20px',
    backgroundColor: 'rgba(255,255,255,0.08)',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    color: '#9CA3AF',
  },
  gateLabel: {
    fontSize: '14px',
    color: '#9CA3AF',
    fontWeight: 500,
  },
  speakerLabel: {
    fontSize: '16px',
    color: '#ffffff',
    fontWeight: 500,
  },
  captionBanner: {
    position: 'absolute',
    bottom: '60px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '90%',
    maxWidth: '1100px',
    padding: '16px 24px',
    borderRadius: '12px',
    backgroundColor: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  captionLine: {
    fontSize: '18px',
    lineHeight: 1.4,
    color: '#ffffff',
    fontWeight: 400,
    textAlign: 'center',
    transition: 'opacity 0.3s ease',
  },
  errorOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(10,10,15,0.9)',
    zIndex: 200,
  },
  errorCard: {
    padding: '32px 40px',
    borderRadius: '16px',
    backgroundColor: '#1a1a2e',
    border: '1px solid #EF4444',
    maxWidth: '500px',
    textAlign: 'center',
  },
  errorTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#EF4444',
    marginBottom: '12px',
  },
  errorMessage: {
    fontSize: '14px',
    color: '#9CA3AF',
    lineHeight: 1.5,
  },
}
